import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  TextField,
  Box,
  Chip,
  Avatar
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { user as userApi } from '../../../utils/api';

const InviteUserDialog = ({ open, onClose, channels, onInviteUsers }) => {
  const { t } = useTranslation(['common']);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Fetch all users
  useEffect(() => {
    const fetchUsers = async () => {
      if (open) {
        try {
          setLoadingUsers(true);
          console.log('Fetching users for invite dialog');
          const response = await userApi.getUsers();
          console.log('User API response:', response);
          
          // Handle response properly - response is now directly the array of users
          const users = Array.isArray(response) ? response : [];
          setAllUsers(users);
          
          console.log('All users set:', users.length);
        } catch (error) {
          console.error('Error fetching users:', error);
        } finally {
          setLoadingUsers(false);
        }
      }
    };

    fetchUsers();
  }, [open]);

  // Filter available users when channel changes
  useEffect(() => {
    console.log('Channel selection changed to:', selectedChannel);
    console.log('All users array length:', allUsers.length);
    
    if (selectedChannel && allUsers.length > 0) {
      const channel = channels.find(c => c._id === selectedChannel);
      console.log('Selected channel:', channel);
      
      if (channel && channel.members && Array.isArray(channel.members)) {
        // Filter out users who are already members
        // Make sure to convert IDs to strings for consistent comparison
        const currentMemberIds = channel.members.map(member => 
          typeof member === 'object' && member._id ? String(member._id) : String(member)
        );
        console.log('Current member IDs:', currentMemberIds);
        
        const filteredUsers = allUsers.filter(user => {
          // Make sure to convert ID to string for consistent comparison
          const userId = String(user._id);
          return !currentMemberIds.includes(userId);
        });
        
        console.log('Filtered users count:', filteredUsers.length);
        setAvailableUsers(filteredUsers);
      } else {
        console.log('No members array found in channel or channel not found');
        setAvailableUsers(allUsers);
      }
    } else {
      setAvailableUsers([]);
    }
    
    // Reset selected users when channel changes
    setSelectedUsers([]);
  }, [selectedChannel, allUsers, channels]);

  const handleChannelChange = (event) => {
    setSelectedChannel(event.target.value);
  };

  const handleClose = () => {
    setSelectedChannel('');
    setSelectedUsers([]);
    onClose();
  };

  const handleInvite = async () => {
    if (!selectedChannel || selectedUsers.length === 0) return;
    
    try {
      setLoading(true);
      await onInviteUsers(selectedChannel, selectedUsers.map(user => user._id));
      handleClose();
    } catch (error) {
      console.error('Error inviting users:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('comms.inviteUser')}</DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('comms.inviteUserDescription')}
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t('comms.selectChannel')}</InputLabel>
          <Select
            value={selectedChannel}
            onChange={handleChannelChange}
            disabled={loading}
            label={t('comms.selectChannel')}
          >
            {channels
              .filter(channel => channel.type === 'team') // Only show team channels
              .map(channel => (
                <MenuItem key={channel._id} value={channel._id}>
                  {channel.name}
                </MenuItem>
              ))
            }
          </Select>
        </FormControl>
        
        <FormControl fullWidth>
          <Autocomplete
            multiple
            id="users-select"
            options={availableUsers}
            loading={loadingUsers}
            disabled={!selectedChannel || loading || loadingUsers}
            value={selectedUsers}
            onChange={(event, newValue) => {
              setSelectedUsers(newValue);
            }}
            getOptionLabel={(option) => option.name || option.username}
            noOptionsText={!selectedChannel 
              ? t('comms.selectChannelFirst') 
              : loadingUsers
                ? t('comms.loadingUsers')
                : availableUsers.length === 0
                  ? t('comms.noAvailableUsers')
                  : t('comms.noMatchingUsers')}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    src={option.imageUrl} 
                    alt={option.name || option.username}
                    sx={{ width: 24, height: 24, mr: 1 }}
                  >
                    {(option.name || option.username || '?')[0].toUpperCase()}
                  </Avatar>
                  <Typography>{option.name || option.username}</Typography>
                </Box>
              </li>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  avatar={
                    <Avatar 
                      src={option.imageUrl}
                      alt={option.name || option.username}
                    >
                      {(option.name || option.username || '?')[0].toUpperCase()}
                    </Avatar>
                  }
                  label={option.name || option.username}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('comms.selectUsers')}
                placeholder={selectedChannel ? t('comms.selectUsers') : t('comms.selectChannelFirst')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                helperText={selectedChannel && availableUsers.length === 0 && !loadingUsers 
                  ? t('comms.allUsersAlreadyInChannel') 
                  : null}
              />
            )}
          />
        </FormControl>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('cancel')}
        </Button>
        <Button 
          onClick={handleInvite} 
          variant="contained" 
          color="primary"
          disabled={loading || !selectedChannel || selectedUsers.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t('inviting') : t('invite')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteUserDialog; 